// @ts-strict-ignore
import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  Button,
  DataTable,
  DataTableCellVariants,
  DataTableHeaderProps,
  DataTableRowData,
  DropdownMenu,
  DropdownMenuItem,
  Highlight,
  Icon,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { DateFormat } from 'constants/General'
import { usePendingAgreementsContext } from 'hooks/context/usePendingAgreementsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { LegalAgreementDetailsType, LegalAgreementStatus, LegalAgreementTableType } from './types'
import { getIsAgreementAssignedToUser } from './utils'

export type LegalAgreementsTableProps = {
  agreementsList: LegalAgreementTableType[]
  isLoading: boolean
  onOpenAgreementDetails: (index: number) => void
}

export const LegalAgreementsTable = ({
  agreementsList,
  isLoading,
  onOpenAgreementDetails,
}: LegalAgreementsTableProps) => {
  const { t } = useTranslation()
  const { profile } = useProfileContext()
  const { setShouldRefetch, setShowSingleAgreementId } = usePendingAgreementsContext()

  const columns: DataTableHeaderProps[] = [
    { key: 'agreementName', variant: DataTableCellVariants.TEXT, label: t('legalAgreements.name') },
    { key: 'dateAccepted', variant: DataTableCellVariants.TEXT, label: t('dateAccepted') },
    { key: 'assignedTo', variant: DataTableCellVariants.TEXT, label: t('assignedTo') },
    { key: 'status', variant: DataTableCellVariants.TEXT, label: t('status') },
    { key: 'actions', variant: DataTableCellVariants.ACTION, label: '' },
  ]

  const tableData: DataTableRowData[] = useMemo(() => {
    if (!agreementsList?.length) return []

    return agreementsList.map((agreement, index) => {
      const agreementDetails: LegalAgreementDetailsType = agreement?.agreement

      const dateSigned =
        agreement?.status === LegalAgreementStatus?.SIGNED && !!agreement?.lastUpdated
          ? dayjs(agreement?.lastUpdated).format(DateFormat.DD_MMM_YYYY)
          : ''

      const isSigned = agreement?.status === LegalAgreementStatus.SIGNED
      const assignedTo = agreement?.user?.emailAddress ?? ''
      const isAssignedToUser = getIsAgreementAssignedToUser(agreement, profile)

      return {
        id: agreementDetails?.reference,
        testid: agreementDetails?.reference,
        onRowClick: () => onOpenAgreementDetails(index),
        cells: [
          {
            key: 'agreementName',
            variant: DataTableCellVariants.TEXT,
            Text: agreementDetails?.displayName,
          },
          {
            key: 'dateAccepted',
            variant: DataTableCellVariants.TEXT,
            Text: dateSigned,
          },
          {
            key: 'assignedTo',
            variant: DataTableCellVariants.TEXT,
            Text: assignedTo,
          },
          {
            key: 'status',
            variant: DataTableCellVariants.HIGHLIGHT,
            Highlights: [
              <Highlight
                key={`${agreementDetails?.reference}-status-${agreement?.statusPill?.label}`}
                type={agreement?.statusPill?.state}
              >
                {agreement?.statusPill?.label}
              </Highlight>,
            ],
          },
          {
            key: 'actions',
            variant: DataTableCellVariants.ACTION,
            Actions: [
              <DropdownMenu
                key={`context-menu-${agreement?.reference}`}
                DropdownMenuItems={[
                  <DropdownMenuItem
                    key={`details-${agreementDetails?.reference}`}
                    LeadingIcon={<Icon name="Info" />}
                    onClick={() => onOpenAgreementDetails(index)}
                  >
                    {t('legalAgreements.details')}
                  </DropdownMenuItem>,
                  ...(isSigned
                    ? [
                        <DropdownMenuItem
                          key={`download-${agreementDetails?.reference}`}
                          LeadingIcon={<Icon name="DownloadSimple" />}
                          onClick={() => window.open(agreementDetails?.url)}
                        >
                          {t('download')}
                        </DropdownMenuItem>,
                      ]
                    : []),
                  ...(!isSigned && isAssignedToUser
                    ? [
                        <DropdownMenuItem
                          key={`reviewAndAccept-${agreementDetails?.reference}`}
                          LeadingIcon={<Icon name="Files" />}
                          onClick={() => {
                            setShowSingleAgreementId(agreement?.id)
                            setShouldRefetch(true)
                          }}
                        >
                          {t('reviewAndAccept')}
                        </DropdownMenuItem>,
                      ]
                    : []),
                ]}
              >
                <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
              </DropdownMenu>,
            ],
          },
        ],
      }
    })
  }, [agreementsList])

  return isLoading ? (
    <RowsSkeletonLoader rows={10} />
  ) : (
    <DataTable headers={columns} data={tableData} />
  )
}
